import { z } from 'zod';
import { tenantSlugSchema } from './tenancy.contract';

export const USER_STATUSES = ['PENDING', 'ACTIVE', 'SUSPENDED', 'LOCKED', 'ARCHIVED'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/**
 * Password policy.
 *
 * There is no composition requirement (mandatory symbols reliably produce
 * `Password1!`). The real defenses are Argon2id hashing, rate limiting and
 * lockout.
 */
export const passwordSchema = z
  .string()
  .min(6, 'Password must be at least 6 characters')
  .max(256, 'Password must not exceed 256 characters');

export const loginRequestSchema = z.object({
  email: z.email().max(320).toLowerCase().trim(),
  password: z.string().min(1).max(256),
  /** Optional: pre-selects a tenant when the user belongs to several. */
  tenantSlug: z.string().trim().max(100).nullish(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/**
 * Church self-registration.
 *
 * One call provisions the tenant, its first campus, the owner's account and
 * a module subscription, then logs the owner straight in — see
 * `RegistrationService`. `moduleKeys` is the church's chosen bundle beyond
 * the free baseline (core modules plus `PEOPLE`, always included); it's
 * validated as real, purchasable, dependency-satisfied modules by the
 * service, not here, since the catalog is module-registry-service's data,
 * not a contract. An empty array is valid — the free baseline alone.
 */
export const registerRequestSchema = z.object({
  churchName: z.string().trim().min(1).max(200),
  slug: tenantSlugSchema,
  ownerName: z.string().trim().min(1).max(200),
  email: z.email().max(320).toLowerCase().trim(),
  password: passwordSchema,
  moduleKeys: z.array(z.string().trim().toUpperCase().min(1).max(50)).default([]),
  locale: z.string().trim().default('en'),
  timezone: z.string().trim().default('UTC'),
  currency: z.string().trim().length(3).toUpperCase().default('USD'),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const refreshRequestSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: passwordSchema,
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

// --- MFA ----------------------------------------------------------------------

export const MFA_FACTOR_TYPES = ['TOTP', 'SMS', 'EMAIL', 'RECOVERY_CODE'] as const;
export type MfaFactorType = (typeof MFA_FACTOR_TYPES)[number];

export const enrollSmsFactorRequestSchema = z.object({
  /** E.164, e.g. `+18095551234`. */
  phoneNumber: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{6,14}$/, 'Expected an E.164 phone number'),
});
export type EnrollSmsFactorRequest = z.infer<typeof enrollSmsFactorRequestSchema>;

export const confirmFactorRequestSchema = z.object({
  credentialId: z.uuid(),
  code: z.string().trim().min(4).max(10),
});
export type ConfirmFactorRequest = z.infer<typeof confirmFactorRequestSchema>;

export const mfaChallengeRequestSchema = z.object({
  mfaToken: z.string().min(1),
  factorType: z.enum(MFA_FACTOR_TYPES),
  code: z.string().trim().min(4).max(20),
});
export type MfaChallengeRequest = z.infer<typeof mfaChallengeRequestSchema>;

/** Sends (or resends) the emailed or texted code for a login challenge. */
export const sendMfaChallengeRequestSchema = z.object({
  mfaToken: z.string().min(1),
  factorType: z.enum(['SMS', 'EMAIL']),
});
export type SendMfaChallengeRequest = z.infer<typeof sendMfaChallengeRequestSchema>;

export const disableFactorRequestSchema = z.object({
  currentPassword: z.string().min(1).max(256),
});
export type DisableFactorRequest = z.infer<typeof disableFactorRequestSchema>;

export interface MfaFactorSummary {
  readonly id: string;
  readonly type: MfaFactorType;
  readonly label: string | null;
  /** Last four digits only, for SMS. Null for every other type. */
  readonly maskedPhoneNumber: string | null;
  readonly verifiedAt: string | null;
  readonly createdAt: string;
}

export interface TotpEnrollmentResponse {
  readonly credentialId: string;
  /** `otpauth://` URI — render it as a QR code. */
  readonly provisioningUri: string;
  /** Base32 secret, shown once, for an authenticator app that cannot scan. */
  readonly secret: string;
}

/** Backup codes are shown exactly once, at the moment they are generated. */
export interface BackupCodesResponse {
  readonly codes: readonly string[];
}

/**
 * A factor was just confirmed. `backupCodes` is set only when this was the
 * account's very first verified factor — the one moment backup codes can be
 * shown at all.
 */
export interface FactorConfirmedResponse {
  readonly backupCodes: readonly string[] | null;
}

/**
 * What `login()` returns instead of tokens when the account has MFA
 * enabled. `mfaToken` is submitted back to `/auth/mfa/challenge`, never used
 * as a bearer token — see `AccessTokenClaims.purpose`.
 */
export interface MfaRequiredResponse {
  readonly mfaRequired: true;
  readonly mfaToken: string;
  readonly availableFactors: readonly MfaFactorType[];
}

export type LoginResult = LoginResponse | MfaRequiredResponse;

export const OAUTH_PROVIDERS = [
  'google',
  'microsoft',
  'apple',
  'facebook',
  'github',
  'linkedin',
] as const;
export type OAuthProviderSlug = (typeof OAUTH_PROVIDERS)[number];

/** `true` for a provider whose credentials are actually configured — the
 *  web client renders a button only for those. */
export type OAuthProvidersResponse = Record<OAuthProviderSlug, boolean>;

export const oauthHandoffRequestSchema = z.object({
  code: z.string().min(1),
});
export type OAuthHandoffRequest = z.infer<typeof oauthHandoffRequestSchema>;

// --- Platform OAuth provider administration ----------------------------------

/** Which of a provider's credentials actually took effect. A saved,
 *  enabled database row overrides the matching `*_OAUTH_*` env vars, the
 *  same precedence `SmtpSettingsSource` already establishes for SMTP. */
export const OAUTH_CREDENTIALS_SOURCES = ['database', 'environment', 'none'] as const;
export type OAuthCredentialsSource = (typeof OAUTH_CREDENTIALS_SOURCES)[number];

export interface OAuthProviderCredentialsSummary {
  readonly provider: OAuthProviderSlug;
  /** The operator's on/off switch — independent of whether credentials are
   *  actually saved. A provider can hold real values and still be off. */
  readonly enabled: boolean;
  /** Whether this provider is actually usable right now: enabled with
   *  complete credentials (database), or an env-configured fallback. */
  readonly active: boolean;
  readonly source: OAuthCredentialsSource;
  readonly clientId: string | null;
  /** Never the secret itself — only whether one is stored. */
  readonly hasClientSecret: boolean;
  /** Apple-only; null for every other provider. */
  readonly teamId: string | null;
  readonly keyId: string | null;
  readonly hasPrivateKey: boolean;
  readonly updatedAt: string | null;
  readonly updatedByUserId: string | null;
}

/**
 * Saves one provider's row. `clientId` is required every time (it is not
 * secret); `clientSecret`/`privateKey` are omitted to keep whatever is
 * already stored, the same "blank means unchanged" convention
 * `UpdateSmtpSettingsRequest.password` uses. Apple additionally requires
 * `teamId`/`keyId` — enforced by the service, not this schema, since the
 * shape genuinely differs by provider.
 */
export const updateOAuthProviderCredentialsRequestSchema = z.object({
  enabled: z.boolean(),
  clientId: z.string().trim().min(1).max(255),
  clientSecret: z.string().min(1).max(4000).optional(),
  teamId: z.string().trim().min(1).max(255).optional(),
  keyId: z.string().trim().min(1).max(255).optional(),
  privateKey: z.string().min(1).max(8000).optional(),
});
export type UpdateOAuthProviderCredentialsRequest = z.infer<
  typeof updateOAuthProviderCredentialsRequestSchema
>;

export const passwordResetRequestSchema = z.object({
  email: z.email().max(320).toLowerCase().trim(),
});
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetConfirmRequestSchema = z.object({
  token: z.string().min(1),
  newPassword: passwordSchema,
});
export type PasswordResetConfirmRequest = z.infer<typeof passwordResetConfirmRequestSchema>;

export const verifyEmailRequestSchema = z.object({
  token: z.string().min(1),
});
export type VerifyEmailRequest = z.infer<typeof verifyEmailRequestSchema>;

export const selectTenantRequestSchema = z.object({
  tenantId: z.uuid(),
});
export type SelectTenantRequest = z.infer<typeof selectTenantRequestSchema>;

// --- Responses --------------------------------------------------------------

export interface TenantMembershipSummary {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly tenantSlug: string;
  readonly roles: readonly string[];
  readonly isOwner: boolean;
}

/**
 * The login response.
 *
 * `accessToken` carries no tenant claim until a tenant is selected: a user with
 * memberships in several churches must make that choice explicitly, so no
 * request can accidentally execute against the wrong one.
 */
export interface AuthTokensResponse {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly tokenType: 'Bearer';
  readonly expiresIn: number;
}

export interface LoginResponse extends AuthTokensResponse {
  readonly user: AuthenticatedUserResponse;
  readonly memberships: readonly TenantMembershipSummary[];
  /** True when the token already carries a tenant claim. */
  readonly tenantSelected: boolean;
}

export interface AuthenticatedUserResponse {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly locale: string;
  readonly timezone: string;
  readonly status: UserStatus;
  readonly mfaEnabled: boolean;
}

/** `GET /auth/me` — everything the web shell needs to render for this session. */
export interface SessionContextResponse {
  readonly user: AuthenticatedUserResponse;
  readonly tenant: {
    readonly id: string;
    readonly name: string;
    readonly slug: string;
    readonly defaultLocale: string;
    readonly defaultTimezone: string;
  } | null;
  readonly memberships: readonly TenantMembershipSummary[];
  readonly permissions: readonly string[];
  readonly roles: readonly string[];
  /** Module keys installed and entitled for this tenant; drives navigation. */
  readonly enabledModules: readonly string[];
  readonly features: readonly string[];
}
